/* eslint-disable max-lines */
import * as React from "react";
import { Form } from "semantic-ui-react";
import * as moment from "moment";
import { createValidator } from "not-valid";

import { Input } from "@neworbit/simpleui-input";

import { MedicalOutcome, MedicalType, MedicalTypeIds } from "@teamprevent/common/healthSurveillance/models";
import { CaseType } from "@teamprevent/common/model";
import { TaskTypes } from "@teamprevent/common/task/taskActions/models";

import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";

import { ohCaseApi } from "../../crud/ohCase";
import { ImmunisationType } from "../../crud/immunisationType";
import { ImmunisationTypeTask } from "../../crud/immunisationTypeTask";
import { TaskType, AddTaskFormModel, MedicalOutcomes } from "../../task/model";
import { getUserDisplayNameWithTeamAndProfession } from "../../crud/adminUserWithTeams/model";
import { AdminUserListModelWithTeams } from "../../crud/adminUserWithTeams/model";
import { EppDecision, EppDecisions } from "../../immunisations/model";

interface Props {
    onChange: (createTask: AddTaskFormModel, isValid: boolean) => void;
    taskTypes: TaskType[];
    immunisationTypes?: ImmunisationType[];
    immunisationTypeTasks?: ImmunisationTypeTask[];
    showErrors: boolean;
    ohCaseId: string;
    task: AddTaskFormModel;
    defaultDueDate: boolean;
    notesLabel?: string;
    medicalTypes?: MedicalType[];
    caseType?: string;
    hideAssignmentOption?: boolean;
    assignmentOptionRequired?: boolean;
    adminUsers?: AdminUserListModelWithTeams[];
    medicalOutcomes?: MedicalOutcomes;
    filterMedicalTypes?: boolean;
}

interface State {
    values: AddTaskFormModel;
    valid: { [K in keyof AddTaskFormModel]: boolean };
    shouldNotValidateDueDate: boolean;
    medicalTypes?: MedicalType[];
}

const getSubTaskOptions = (subTasks: TaskType["subTasks"]) => subTasks.map(type => ({ key: type, value: type, text: type }));

export class AddTaskForm extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        const taskType = this.props.taskTypes.find(t => t.type === props.task.taskType?.type);
        const isDueDateValid = this.isDueDateValid(props.task.taskType, props.task.dueDate);

        this.state = {
            values: props.task,
            valid: {
                taskType: !!taskType,
                taskCode: true,
                dueDate: this.props.defaultDueDate || isDueDateValid,
                notes: true,
                immunisationTypeTaskIds: true,
                eppDecision: true,
                caseType: true,
                medicalIds: true,
                assignedUsers: props.assignmentOptionRequired ? false : true
            },
            shouldNotValidateDueDate: (props.caseType === CaseType.IMMUNISATION || props.caseType === CaseType.HEALTH_SURVEILLANCE),
            medicalTypes: props.medicalTypes
        };

        this.props.onChange(this.state.values, this.valid());
    }

    public componentDidUpdate(prevProps: Props) {
        if (this.props.task !== prevProps.task) {
            this.setState({
                values: this.props.task,
                valid: {
                    ...this.state.valid,
                    taskType: !!this.props.task?.taskType
                }
            });
        }

        const isTaskTypeChanged = this.props.task?.taskType !== prevProps.task?.taskType;
        if (this.props.filterMedicalTypes && (isTaskTypeChanged || this.props.medicalOutcomes !== prevProps.medicalOutcomes)) {
            const medicalTypes = this.getMedicalTypes(this.state.values);
            const updatedState = { ...this.state, medicalTypes };
            this.setState(updatedState);
        }
    }

    public render() {
        if (!this.props.taskTypes) {
            return null;
        }

        const medicalTypes = this.state.medicalTypes;
        const immunisationTypes = this.props.immunisationTypes;

        return (
            <>
                <Form.Field>
                    <Input.Dropdown
                        dynamicOptions
                        placeholder="Select Task *"
                        search
                        options={this.props.taskTypes.map(type => ({ key: type.type, value: type.type, text: type.description }))}
                        value={this.state.values.taskType && this.state.values.taskType.type}
                        required
                        showErrors={this.props.showErrors}
                        onChange={this.taskChanged}
                    />
                </Form.Field>
                {this.state.values.taskType && this.state.values.taskType.immunisationTaskType &&
                    <Form.Field>
                        <Input.DropdownMulti
                            dynamicOptions
                            placeholder="Immunisation Types *"
                            search
                            options={this.props.immunisationTypeTasks?.filter(t => t.taskType === this.state.values.taskType.immunisationTaskType)
                                .map(imm => {
                                    const immsType = immunisationTypes?.find(it => it.id === imm.immunisationTypeId);
                                    return { value: imm.id, text: immsType?.name ? `${immsType.name} - ${imm.name}` : imm.name };
                                }) || []}
                            value={this.state.values.immunisationTypeTaskIds}
                            required
                            showErrors={this.props.showErrors}
                            onChange={this.immunisationTypesChanged}
                        />
                    </Form.Field>}
                {this.state.values.taskType && this.state.values.taskType.type === TaskTypes.AllImmunisationsCompletedForRoleTask &&
                    <Form.Field>
                        <Input.Dropdown
                            label="Epp Decision *"
                            placeholder="Epp Decision"
                            options={EppDecisions}
                            value={this.state.values.eppDecision && this.state.values.eppDecision}
                            onChange={this.eppDecisionChanged}
                            showErrors={this.props.showErrors}
                            required
                        />
                    </Form.Field>}
                {this.state.values.taskType && this.state.values.taskType.subTasks?.length > 0 &&
                    <Form.Field>
                        <Input.Dropdown
                            dynamicOptions
                            placeholder="Select Task *"
                            search
                            options={getSubTaskOptions(this.state.values.taskType.subTasks)}
                            value={this.state.values.taskCode}
                            required
                            showErrors={this.props.showErrors}
                            onChange={this.taskCodeChanged}
                        />
                    </Form.Field>}
                {this.state.values.taskType && medicalTypes?.length > 0 &&
                    <Form.Field>
                        <Input.DropdownMulti
                            label="Medicals *"
                            placeholder="Select Medical Types *"
                            search
                            options={medicalTypes.map(m => ({ text: m.name, value: m.id }))}
                            value={this.state.values.medicalIds}
                            required
                            showErrors={this.props.showErrors}
                            onChange={this.medicalIdsChanged}
                            dynamicOptions
                        />
                    </Form.Field>
                }
                {this.state.values.taskType && this.state.values.taskType.dueDateApplicable &&
                    <Form.Field>
                        {this.state.shouldNotValidateDueDate ?
                            <Input.Date
                                label="Due Date *"
                                onChange={this.dueDateChanged}
                                value={this.state.values.dueDate}
                                validation={[m => createValidator((value: moment.Moment) => this.isInTheFuture(value), "Must be in the future")(m)]}
                                required={this.state.values.taskType && this.state.values.taskType.dueDateApplicable}
                                showErrors={this.props.showErrors}
                            />
                            :
                            <Input.Date
                                label="Due Date *"
                                onChange={this.dueDateChanged}
                                value={this.state.values.dueDate}
                                validation={[
                                    m => createValidator((value: moment.Moment) => this.isInTheFuture(value), "Must be in the future")(m),
                                ]}
                                required={this.state.values.taskType && this.state.values.taskType.dueDateApplicable}
                                showErrors={this.props.showErrors}
                            />
                        }
                    </Form.Field>}
                {!this.props.hideAssignmentOption &&
                    <Input.Dropdown
                        dynamicOptions
                        placeholder={"Select Users" + (this.props.assignmentOptionRequired ? " *" : "")}
                        value={this.state.values.assignedUsers?.length > 0 ? this.state.values.assignedUsers[0].id : null}
                        label={"Assigned to" + (this.props.assignmentOptionRequired ? " *" : "")}
                        showErrors={this.props.showErrors}
                        onChange={this.setAssignedUser}
                        options={this.props.adminUsers?.map(adminUser =>
                            ({ key: adminUser.id, value: adminUser.id, text: getUserDisplayNameWithTeamAndProfession(adminUser) }))}
                        search
                        required={!!this.props.assignmentOptionRequired}
                    />
                }
                <div className="text-snippet-container-padded">
                    <TextSnippetSelector onSelect={this.updateNotes} />
                </div>
                <Form.Field>
                    <Input.Textarea
                        label={`${this.props.notesLabel || "Notes"} `}
                        onChange={this.notesChanged}
                        value={this.state.values.notes}
                        required={this.state.values.taskType?.notesRequired}
                    />
                </Form.Field>
            </>
        );
    }

    private getMedicalTypes(task: AddTaskFormModel): MedicalType[] {
        const isBookRepeatMedicalConsultationTask = task?.taskType?.type === TaskTypes.BookRepeatMedicalConsultationTask;
        const isBookRepeatMedicalTaskButNoDecisionPendingOutcome = isBookRepeatMedicalConsultationTask &&
            (this.props.medicalOutcomes ?? []).some(mo =>
                mo.medicalType.id === MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1 &&
                mo.values.outcome !== MedicalOutcome.decisionPending);

        if (this.props.filterMedicalTypes && isBookRepeatMedicalTaskButNoDecisionPendingOutcome) {
            const medicalTypes = (this.props.medicalTypes ?? []).filter(mt => mt.id !== MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1);
            return medicalTypes;
        }

        return this.props.medicalTypes ?? [];
    }

    private updateNotes = (snippetText: string) => {
        this.notesChanged(this.state.values.notes + " " + snippetText, true);
    };

    private checkForLastAssignedUser = async (taskType: TaskType) => {
        const user = await ohCaseApi.getLastTaskAssignedUser(taskType, this.props.ohCaseId);

        if (user) {
            const adminUser = this.props.adminUsers.find(a => a.id === user);

            return [{
                id: adminUser.id,
                name: getUserDisplayNameWithTeamAndProfession(adminUser)
            }];
        }

        return [];
    };

    private taskChanged = async (task: string) => {
        // this is added because the function gets re rendered to many times so async functions within the event will cause state setting issues.
        if (task === this.state.values.taskType?.type || !task) {
            return;
        }

        const taskType = this.props.taskTypes.find(t => t.type === task);
        const taskCode = this.state.values.taskType && this.state.values.taskType.type === task ? this.state.values.taskCode : null;

        let dueDate = this.state.values.dueDate?.clone();
        if (this.props.defaultDueDate && taskType) {
            if (!taskType.dueDateApplicable) {
                dueDate = null;
            } else if (!this.state.values.dueDate) {
                dueDate = moment().startOf("day");
            }
        }

        const isDueDateValid = this.isDueDateValid(taskType, dueDate);
        const valid = {
            ...this.state.valid,
            taskType: !!taskType,
            taskCode: !taskType || !taskType.subTasks || taskType.subTasks?.length === 0 || !!taskCode,
            dueDate: isDueDateValid,
            immunisationTypeTaskIds: !taskType?.immunisationTaskType || this.state.values.immunisationTypeTaskIds?.length > 0
        };

        const values = { ...this.state.values, taskType, taskCode, dueDate };

        const assignedUsers = this.props.hideAssignmentOption
            ? this.state.values.assignedUsers :
            await this.checkForLastAssignedUser(taskType);

        if (taskType?.type === TaskTypes.NoFurtherActionTask || taskType?.type === TaskTypes.AllImmunisationsCompletedForRoleTask) {
            valid.assignedUsers = true;
            values.assignedUsers = [{ id: "Unassigned", name: "Unassigned" }];
        }
        else if (taskType) {
            const userAssigned = assignedUsers?.length > 0;
            valid.assignedUsers = userAssigned;
            values.assignedUsers = userAssigned ? assignedUsers : [];
        }

        this.setState({
            values,
            valid
        }, () => this.props.onChange(this.state.values, this.valid()));
    };

    private taskCodeChanged = (taskCode: string, isValid: boolean) => {
        this.setState({
            values: { ...this.state.values, taskCode },
            valid: {
                ...this.state.valid,
                taskCode: isValid
            }
        }, () => this.props.onChange(this.state.values, this.valid()));
    };

    private eppDecisionChanged = (eppDecision: EppDecision, valid: boolean) => {
        this.setState( {
            values: { ...this.state.values, eppDecision },
            valid: {
                ...this.state.valid,
                eppDecision: valid
            }
        }, () => this.props.onChange(this.state.values, this.valid()));
    };

    private immunisationTypesChanged = (immunisationTypeTaskIds: string[], isValid: boolean) => {
        this.setState({
            values: { ...this.state.values, immunisationTypeTaskIds },
            valid: {
                ...this.state.valid,
                immunisationTypeTaskIds: isValid
            }
        }, () => this.props.onChange(this.state.values, this.valid()));
    };

    private medicalIdsChanged = (medicalIds: string[], isValid: boolean) => {
        this.setState({
            values: { ...this.state.values, medicalIds },
            valid: {
                ...this.state.valid,
                medicalIds: isValid
            }
        }, () => this.props.onChange(this.state.values, this.valid()));
    };

    private isInTheFuture = (value: moment.Moment) => {
        return value && value.isSameOrAfter(moment().startOf("day"));
    };

    private valid = () => {
        return !(Object.keys(this.state.valid) as (keyof AddTaskFormModel)[]).some(k => !this.state.valid[k]);
    };

    private dueDateChanged = (value: moment.Moment, isValid: boolean) => {
        this.setState({
            values: { ...this.state.values, dueDate: value },
            valid: { ...this.state.valid, dueDate: isValid }
        }, () => this.props.onChange(this.state.values, this.valid()));
    };

    private notesChanged = (value: string, isValid: boolean) => {
        this.setState({
            values: { ...this.state.values, notes: value },
            valid: { ...this.state.valid, notes: isValid }
        }, () => this.props.onChange(this.state.values, this.valid()));
    };

    private setAssignedUser = (assignedUserId: string, isValid: boolean) => {
        const adminUser = this.props.adminUsers.find(a => a.id === assignedUserId);
        const valid = {
            ...this.state.valid,
            assignedUsers: isValid
        };
        this.setState({
            values: {
                ...this.state.values, assignedUsers:
                    adminUser ? [{ id: adminUser.id, name: getUserDisplayNameWithTeamAndProfession(adminUser) }] : []
            },
            valid
        }, () => this.props.onChange(this.state.values, this.valid()));
    };

    private isDueDateValid(taskType: Pick<TaskType, "dueDateApplicable"> | null, dueDate: moment.Moment) {
        return (taskType?.dueDateApplicable ? this.isInTheFuture(dueDate) : true) && (!taskType || !taskType.dueDateApplicable || !!dueDate);
    }
}
